import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Avatar,
  MenuItem,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";

const PDIAssignmentModal = ({
  pdiData,
  getPDIList,
  initialpdiAssignedTo,
  onChange,
  users,
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Set initial assigned user
  useEffect(() => {
    if (initialpdiAssignedTo && users.length > 0) {
      const assignedUser = users.find(
        (user) => user._id === initialpdiAssignedTo
      );
      setSelectedUser(assignedUser || null);
    } else {
      setSelectedUser(null);
    }
  }, [initialpdiAssignedTo, users]);

  const handleUserChange = async (event, newValue) => {
    if (!isEditable || !pdiData?._id) {
      console.error("PDI data missing or not editable");
      return;
    }

    setUpdating(true);
    const previousUser = selectedUser;

    try {
      setSelectedUser(newValue);

      const token = localStorage.getItem("token");
      const currentUserId = localStorage.getItem("userId");

      const response = await axios.put(
        `${process.env.REACT_APP_URL}/pdi/edit/${pdiData._id}`,
        {
          pdiAssignedTo: newValue?._id || null,
          pdiAssignedBy: newValue ? currentUserId : null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.success) {
        const successMessage = newValue
          ? `PDI assigned to ${newValue.name} successfully`
          : "PDI assignment removed successfully";

        showSnackbar(successMessage, "success");
        onChange?.(newValue);
        getPDIList?.();
      } else {
        throw new Error(
          response?.data?.message || "Failed to update assignment"
        );
      }
    } catch (err) {
      console.error("Error assigning PDI:", err);
      setSelectedUser(previousUser);

      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to assign PDI. Please try again.";
      showSnackbar(errorMessage, "error");
    } finally {
      setUpdating(false);
    }
  };

  // Check if PDI is in editable state
  const isEditable = !["Approved", "Rejected"].includes(pdiData?.pdiStatus);

  return (
    <>
      <Box
        sx={{
          minWidth: 200,
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Autocomplete
          size="small"
          options={users}
          getOptionLabel={(option) => option.name || "Unnamed User"}
          value={selectedUser}
          onChange={handleUserChange}
          loading={updating}
          disabled={!isEditable || updating || users.length === 0}
          sx={{
            width: "100%",
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={isEditable ? "Assign to KAM" : "Not editable"}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  appearance: "none",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.5rem",
                  paddingRight: "2.5rem",
                  backgroundColor: "#fff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#93c5fd",
                  },
                  "&.Mui-focused": {
                    borderColor: "#3b82f6",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e5e7eb",
                    cursor: "not-allowed",
                  },
                  "& fieldset": { border: "none" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  padding: "0.625rem 0.75rem",
                  color: "#374151",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {updating && <CircularProgress color="inherit" size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const isSelected = selectedUser?._id === option._id;
            return (
              <MenuItem
                {...props}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1,
                  "&:hover": {
                    backgroundColor: isSelected ? "#E3F2FD" : "#f5f5f5",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    mr: 1,
                    bgcolor: isSelected ? "primary.main" : "grey.300",
                    fontSize: 12,
                    fontWeight: "bold",
                  }}
                >
                  {option.name?.[0]?.toUpperCase() || "U"}
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: "0.9rem" }}>
                    {option.name || "Unnamed User"}
                  </div>
                </Box>

                <Checkbox
                  checked={isSelected}
                  size="small"
                  sx={{
                    ml: "auto",
                    "&.Mui-checked": { color: "primary.main" },
                  }}
                />
              </MenuItem>
            );
          }}
          isOptionEqualToValue={(option, value) => option._id === value._id}
        />

        {users?.length === 0 && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              backgroundColor: "#fff3e0",
              borderRadius: 1,
              border: "1px solid #ffb74d",
              fontSize: "0.75rem",
              color: "#e65100",
            }}
          >
            No key account managers available
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PDIAssignmentModal;
